import type { CardTypeEnum } from "@sorcery/db";
import { CardCategoryEnum, CardRarityEnum, DeckFormatEnum } from "@sorcery/db";

const LIMIT_OVERRIDES = [
  "relentless_crowd",
  "grey_wolves",
  "spire",
  "stream",
  "valley",
  "wasteland",
];

type DeckRuleCheckType = {
  decklist: {
    card: {
      name: string;
      slug: string;
      guardian: {
        type: CardTypeEnum;
        category: CardCategoryEnum | null;
        rarity: CardRarityEnum;
      } | null;
    };
    quantity: number;
  }[];
  sideboard: {
    card: {
      name: string;
      guardian: {
        category: CardCategoryEnum | null;
      } | null;
    };
    quantity: number;
  }[];
};

const CONSTRUCTED_SPELLBOOK_SIZE = 50;
const CONSTRUCTED_ATLAS_SIZE = 30;

const LIMITED_SPELLBOOK_SIZE = 24;
const LIMITED_ATLAS_SIZE = 12;

export const deckbuildingRuleViolations = [
  // ALL FORMATS
  // {},
  // CONSTRUCTED
  {
    format: DeckFormatEnum.Constructed,
    name: `Spellbook contains less than ${CONSTRUCTED_SPELLBOOK_SIZE} cards`,
    // returns true if rule is violated
    check: (deck: DeckRuleCheckType) =>
      deck.decklist
        .filter((d) => d.card.guardian?.category === CardCategoryEnum.Spell)
        .reduce((a, c) => a + c.quantity, 0) < CONSTRUCTED_SPELLBOOK_SIZE,
  },
  {
    format: DeckFormatEnum.Constructed,
    name: `Atlas contains less than ${CONSTRUCTED_ATLAS_SIZE} cards`,
    check: (deck: DeckRuleCheckType) =>
      deck.decklist
        .filter((d) => d.card.guardian?.category === CardCategoryEnum.Site)
        .reduce((a, c) => a + c.quantity, 0) < CONSTRUCTED_ATLAS_SIZE,
  },
  {
    format: DeckFormatEnum.Constructed,
    name: "Sideboard above 10 card limit",
    check: (deck: DeckRuleCheckType) =>
      (deck.sideboard ?? []).reduce((a, c) => a + c.quantity, 0) > 10,
  },
  {
    format: DeckFormatEnum.Constructed,
    name: "Deck contains non playable cards",
    check: (deck: DeckRuleCheckType) =>
      !!deck.decklist.filter(
        (d) =>
          d.card.guardian?.category === CardCategoryEnum.None ||
          d.card.guardian?.category === CardCategoryEnum.Token,
      ).length ||
      !!deck.sideboard.filter(
        (d) =>
          d.card.guardian?.category === CardCategoryEnum.None ||
          d.card.guardian?.category === CardCategoryEnum.Token,
      ).length,
    context: (deck: DeckRuleCheckType) =>
      (
        deck.decklist.filter(
          (d) =>
            d.card.guardian?.category === CardCategoryEnum.None ||
            d.card.guardian?.category === CardCategoryEnum.Token,
        ) as { card: { name: string } }[]
      )
        .concat(
          deck.sideboard.filter(
            (d) =>
              d.card.guardian?.category === CardCategoryEnum.None ||
              d.card.guardian?.category === CardCategoryEnum.Token,
          ),
        )
        .map((c) => c.card.name)
        .join(", "),
  },

  {
    format: DeckFormatEnum.Constructed,
    name: "Above Ordinary 4 copy limit",
    check: (deck: DeckRuleCheckType) =>
      !!deck.decklist.filter(
        (d) =>
          d.card.guardian?.rarity === CardRarityEnum.Ordinary &&
          d.quantity > 4 &&
          !LIMIT_OVERRIDES.includes(d.card.slug),
      ).length,
    context: (deck: DeckRuleCheckType) =>
      deck.decklist
        .filter(
          (d) =>
            d.card.guardian?.rarity === CardRarityEnum.Ordinary &&
            d.quantity > 4 &&
            !LIMIT_OVERRIDES.includes(d.card.slug),
        )
        .map((c) => c.card.name)
        .join(", "),
  },
  {
    format: DeckFormatEnum.Constructed,
    name: "Above Exceptional 3 copy limit",
    check: (deck: DeckRuleCheckType) =>
      !!deck.decklist.filter(
        (d) =>
          d.card.guardian?.rarity === CardRarityEnum.Exceptional &&
          d.quantity > 3 &&
          !LIMIT_OVERRIDES.includes(d.card.slug),
      ).length,
    context: (deck: DeckRuleCheckType) =>
      deck.decklist
        .filter(
          (d) =>
            d.card.guardian?.rarity === CardRarityEnum.Exceptional &&
            d.quantity > 3 &&
            !LIMIT_OVERRIDES.includes(d.card.slug),
        )
        .map((c) => c.card.name)
        .join(", "),
  },

  {
    format: DeckFormatEnum.Constructed,
    name: "Above Elite 2 copy limit",
    check: (deck: DeckRuleCheckType) =>
      !!deck.decklist.filter(
        (d) =>
          d.card.guardian?.rarity === CardRarityEnum.Elite &&
          d.quantity > 2 &&
          !LIMIT_OVERRIDES.includes(d.card.slug),
      ).length,
    context: (deck: DeckRuleCheckType) =>
      deck.decklist
        .filter(
          (d) =>
            d.card.guardian?.rarity === CardRarityEnum.Elite &&
            d.quantity > 2 &&
            !LIMIT_OVERRIDES.includes(d.card.slug),
        )
        .map((c) => c.card.name)
        .join(", "),
  },
  {
    format: DeckFormatEnum.Constructed,
    name: "Above Unique 1 copy limit",
    check: (deck: DeckRuleCheckType) =>
      !!deck.decklist.filter(
        (d) =>
          d.card.guardian?.rarity === CardRarityEnum.Unique &&
          d.quantity > 1 &&
          !LIMIT_OVERRIDES.includes(d.card.slug),
      ).length,
    context: (deck: DeckRuleCheckType) =>
      deck.decklist
        .filter(
          (d) =>
            d.card.guardian?.rarity === CardRarityEnum.Unique &&
            d.quantity > 1 &&
            !LIMIT_OVERRIDES.includes(d.card.slug),
        )
        .map((c) => c.card.name)
        .join(", "),
  },

  // DRAFT
  {
    format: DeckFormatEnum.Draft,
    name: `Spellbook contains less than ${LIMITED_SPELLBOOK_SIZE} cards`,
    // returns true if rule is violated
    check: (deck: DeckRuleCheckType) =>
      deck.decklist
        .filter((d) => d.card.guardian?.category === CardCategoryEnum.Spell)
        .reduce((a, c) => a + c.quantity, 0) < LIMITED_SPELLBOOK_SIZE,
  },
  {
    format: DeckFormatEnum.Draft,
    name: `Atlas contains less than ${LIMITED_ATLAS_SIZE} cards`,
    check: (deck: DeckRuleCheckType) =>
      deck.decklist
        .filter((d) => d.card.guardian?.category === CardCategoryEnum.Site)
        .reduce((a, c) => a + c.quantity, 0) < LIMITED_ATLAS_SIZE,
  },

  // SEALED
  {
    format: DeckFormatEnum.Sealed,
    name: `Spellbook contains less than ${LIMITED_SPELLBOOK_SIZE} cards`,
    // returns true if rule is violated
    check: (deck: DeckRuleCheckType) =>
      deck.decklist
        .filter((d) => d.card.guardian?.category === CardCategoryEnum.Spell)
        .reduce((a, c) => a + c.quantity, 0) < LIMITED_SPELLBOOK_SIZE,
  },
  {
    format: DeckFormatEnum.Sealed,
    name: `Atlas contains less than ${LIMITED_ATLAS_SIZE} cards`,
    check: (deck: DeckRuleCheckType) =>
      deck.decklist
        .filter((d) => d.card.guardian?.category === CardCategoryEnum.Site)
        .reduce((a, c) => a + c.quantity, 0) < LIMITED_ATLAS_SIZE,
  },
];
